import type { MapDocument, PropertySchema, ValidationIssue } from "daedalus-core";
import { THING_SOUND_SETS, type ThingSoundSets } from "./things.ts";

/**
 * A thing's "Sound set" field (ADR-085's amendment of 2026-10-13): how a builder says what a gate sounds like. A
 * behaviour type that lists more than one sound set is given a choice among them, registered with the type as one
 * of its fields, so Daedalus's inspector draws it with no knowledge of sound; the compile writes a choice other
 * than the default to the prop's appearance, which the page passes to the cue. A type with one set has no field.
 */

/**
 * The name of the field on a behaviour's fields.
 */
export const SOUND_SET_FIELD = "soundSet";

/**
 * The field's title in the inspector.
 */
const SOUND_SET_TITLE = "Sound set";

/**
 * What the inspector calls each sound set a builder can choose. A set with no entry is shown by its id.
 */
const SOUND_SET_LABELS: Readonly<Record<string, string>> = Object.freeze({ wooden_door: "Wooden door", iron_gate: "Iron gate", wooden_fence: "Wooden fence" });

/**
 * The Sound set choice of a behaviour type: its sets as the options, the first the default.
 *
 * @param type The behaviour type.
 * @param sets Each type's sound sets; the game's by default.
 * @returns The property, or null for a type that lists fewer than two sets, which is given no field.
 * @example
 * soundSetProperty("gate");
 * // { type: "string", title: "Sound set", enum: ["wooden_door", "iron_gate", "wooden_fence"], default: "wooden_door" }
 */
export function soundSetProperty(type: string, sets: ThingSoundSets = THING_SOUND_SETS): Extract<PropertySchema, { readonly type: "string" }> | null {
  const listed = sets[type] ?? [];
  return listed.length < 2 ? null : { type: "string", title: SOUND_SET_TITLE, enum: [...listed], default: listed[0] as string };
}

/**
 * The labels of a type's Sound set choice, one for each of its sets in order.
 *
 * @param type The behaviour type.
 * @param sets Each type's sound sets; the game's by default.
 * @returns The labels; none for a type that lists fewer than two sets.
 */
export function soundSetLabels(type: string, sets: ThingSoundSets = THING_SOUND_SETS): string[] {
  const listed = sets[type] ?? [];
  return listed.length < 2 ? [] : listed.map((set) => SOUND_SET_LABELS[set] ?? set);
}

/**
 * The Issues a map's things raise for their Sound set, in the editor and at a compile: a value that is none of its
 * type's sets, a warning that never refuses, since the compile writes no sound set for it and the game plays the
 * type's default, with its fix after the semicolon. A refusal would stop every world holding such a gate from
 * compiling the day a set is renamed or removed. A thing with no value sounds as its type's default and raises
 * nothing, and a type with fewer than two sets is not judged.
 *
 * @param map The map.
 * @param labels What each behaviour type is called, by its id; a type with no entry is called by its id.
 * @param sets Each type's sound sets; the game's by default.
 * @returns The issues, in document order.
 */
export function soundSetIssues(map: MapDocument, labels: Readonly<Record<string, string>> = {}, sets: ThingSoundSets = THING_SOUND_SETS): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      const type = prop.behaviour?.type;
      const listed = type === undefined ? [] : (sets[type] ?? []);
      const value = prop.behaviour?.fields[SOUND_SET_FIELD];
      if (type === undefined || listed.length < 2 || value === undefined || (typeof value === "string" && listed.includes(value))) {
        continue;
      }
      const fallback = listed[0] as string;
      issues.push({ mapId: map.id, layerId: layer.id, propId: prop.id, kind: "sound.unknown-set", severity: "warning", ignorable: true, message: `prop ${prop.id} is a ${labels[type] ?? type} whose Sound set ${String(value)} is not one of its type's, so it plays its type's default, ${SOUND_SET_LABELS[fallback] ?? fallback}; choose its Sound set again` });
    }
  }
  return issues;
}
